import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  StandardizedTable,
  TableColumn,
  TableTotal,
  formatCurrency,
  formatNumber,
  TrendIndicator as StdTrendIndicator,
} from "@/components/ui/standardized-table";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  TrendingUp,
  TrendingDown,
  Search,
  Activity,
  Target,
  ImageOff,
} from "lucide-react";
import GlobalFilter from "@/components/common/GlobalFilter";
import { useGlobalFilter } from "@/features/filters/filter.context";
import { formatDateToISO } from "@/features/filters/filter.types";
import { isComparisonEnabled } from "@/features/filters/filter.utils";
import { FilterState } from "@/features/filters/filter.types";
import type {
  GoogleAdsAccountInfo,
  MetaAdsAccount,
} from "@/backend-sdk/client/types.gen";
import {
  getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet,
  getMetaAdsAdPerformanceApiV1IntegrationsMetaAdsAdPerformanceGet,
  getMetaAdsFeaturedAdsetsApiV1IntegrationsMetaAdsFeaturedAdsetsGet,
  getAccountsApiV1IntegrationsMetaAdsAccountsGet,
  proxyAdsDashboardKpiApiV1AdsDashboardPlatformKpisMetricGet,
  proxyAdsDashboardChartApiV1AdsDashboardPlatformChartsChartGet,
  proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet,
} from "@/backend-sdk/client/sdk.gen";
import type {
  MetaAdsAdPerformanceItem,
  MetaAdsAdPerformanceTotalRow,
  MetaAdsFeaturedAdsetItem,
  MetaAdsFeaturedAdsetTotalRow,
  CampaignTableItem,
  ProductTableItem,
  SearchTermTableItem,
  AdsDashboardTableResponseCampaignTableItem,
  AdsDashboardTableResponseProductTableItem,
  AdsDashboardTableResponseSearchTermTableItem,
} from "@/backend-sdk/client/types.gen";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
// LineChart do Tremor removido — gráficos agora usam Recharts com eixo Y duplo
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { TooltipProps } from "recharts";
import type {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import { formatDateBR } from "@/utils/dateFormat";
import { formatPercent, formatSignedPercent } from "@/utils/numberFormat";
import { getKpiTooltip } from "@/lib/kpi-tooltips";
import { KpiNumber } from "@/components/ui/kpi-number";
import { EstadoDoValor } from "@/components/ui/estado-do-valor";
import type {
  FonteDeDados,
  RequisitoDeFonte,
} from "@/lib/motivo-do-indisponivel";

const formatRoas = (value: number) =>
  Number(value || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPercentCell = (value: unknown) => formatPercent(Number(value) || 0);

const renderDeltaPercentCell = (value: unknown) => {
  // <M-091> `null` é "sem base na janela anterior" — o conjunto ou a campanha não tinha
  // ROAS antes e tem agora. `Number(null)` é 0, e a célula desenharia uma variação nula.
  const num = value === null ? Number.NaN : Number(value);
  if (!Number.isFinite(num)) {
    return <span className="text-muted-foreground">—</span>;
  }
  return <StdTrendIndicator value={num} />;
};

// Colunas para a tabela de campanhas Google ADS
const colunasGoogleCampanhas: TableColumn[] = [
  { key: "campanha", header: "Campanha", align: "left" },
  {
    key: "impressoes",
    header: "Impressões",
    align: "right",
    render: (value) => formatNumber(Number(value) || 0),
  },
  {
    key: "cliques",
    header: "Cliques",
    align: "right",
    render: (value) => formatNumber(Number(value) || 0),
  },
  {
    key: "cpcMedio",
    header: "CPC Médio",
    align: "right",
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "ctr",
    header: "CTR",
    align: "right",
    render: (value) => formatPercentCell(value),
  },
  {
    key: "compras",
    header: "Compras",
    align: "right",
    render: (value) => formatNumber(Number(value) || 0),
  },
  {
    key: "cpa",
    header: "CPA",
    align: "right",
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "txConversao",
    header: "TX Conversão",
    align: "right",
    render: (value) => formatPercentCell(value),
  },
  {
    key: "investimento",
    header: "Investimento",
    align: "right",
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "receita",
    header: "Receita",
    align: "right",
    minWidth: 120,
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "roas",
    header: "ROAS",
    align: "right",
    render: (value) => formatRoas(Number(value) || 0),
  },
];

// Colunas para a tabela de campanhas Meta ADS
const colunasMetaCampanhas: TableColumn[] = [
  { key: "campanha", header: "Campanha", align: "left" },
  {
    key: "pagDestino",
    header: "Pág. Destino",
    align: "right",
    render: (value) => formatNumber(Number(value) || 0),
  },
  {
    key: "custoPagDestino",
    header: "Custo Pág. Destino",
    align: "right",
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "txConversao",
    header: "TX Conversão",
    align: "right",
    render: (value) => formatPercentCell(value),
  },
  {
    key: "investimento",
    header: "Investimento",
    align: "right",
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "compras",
    header: "Compras",
    align: "right",
    render: (value) => formatNumber(Number(value) || 0),
  },
  {
    key: "receita",
    header: "Receita",
    align: "right",
    minWidth: 120,
    render: (value) => formatCurrency(Number(value) || 0),
  },
  {
    key: "roas",
    header: "ROAS",
    align: "right",
    render: (value) => formatRoas(Number(value) || 0),
  },
  {
    key: "delta",
    header: "% Δ",
    align: "right",
    render: (value) => renderDeltaPercentCell(value),
  },
];

// ─── Heatmap helpers para tabelas Meta Ads ────────────────────────────────────
const HEATMAP_COLORS: Record<string, { bg: string; text: string }> = {
  investimento: { bg: "rgba(34,197,94,0.25)", text: "#22c55e" },
  compras: { bg: "rgba(168,85,247,0.25)", text: "#a855f7" },
  receita: { bg: "rgba(6,182,212,0.25)", text: "#06b6d4" },
};

function buildHeatmapRenderer(colorKey: string, allValues: number[]) {
  const max = Math.max(...allValues, 1);
  const { bg, text: textColor } = HEATMAP_COLORS[colorKey] || {
    bg: "rgba(255,255,255,0.1)",
    text: "#fff",
  };
  return (value: unknown) => {
    const num = Number(value) || 0;
    const pct = Math.min(num / max, 1);
    return (
      <div
        className="relative flex items-center justify-end h-7 px-2 rounded"
        style={{
          background: bg,
          width: `${Math.max(pct * 100, 15)}%`,
          marginLeft: "auto",
        }}
      >
        <span
          className="relative z-10 whitespace-nowrap text-xs font-medium"
          style={{ color: textColor }}
        >
          {colorKey === "compras" ? formatNumber(num) : formatCurrency(num)}
        </span>
      </div>
    );
  };
}

// Colunas para a tabela de Conjunto de Anúncios (adsets)
function buildColunasAdsets(_data: MetaAdsFeaturedAdsetItem[]): TableColumn[] {
  return [
    { key: "adset_name", header: "Conjunto de Anúncios", align: "left" },
    {
      key: "pagDestino",
      header: "Pág. Destino",
      align: "right",
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "custoPagDestino",
      header: "Custo Pág. Destino",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (v) => formatPercentCell(v),
    },
    {
      key: "investimento",
      header: "Investimento",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "compras",
      header: "Compras",
      align: "right",
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "receita",
      header: "Receita",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "roas",
      header: "ROAS",
      align: "right",
      render: (v) => formatRoas(Number(v) || 0),
    },
    {
      key: "delta",
      header: "% Δ",
      align: "right",
      render: (v) => renderDeltaPercentCell(v),
    },
    // --- Engrenagem ---
    {
      key: "alcance",
      header: "Alcance",
      align: "right",
      visible: false,
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "frequencia",
      header: "Frequência",
      align: "right",
      visible: false,
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      key: "cpcMedio",
      header: "CPC",
      align: "right",
      visible: false,
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "cliquesLink",
      header: "Cliques Link",
      align: "right",
      visible: false,
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "ctr",
      header: "CTR",
      align: "right",
      visible: false,
      render: (v) => formatPercentCell(v),
    },
    {
      key: "ctrTodos",
      header: "CTR Todos",
      align: "right",
      visible: false,
      render: (v) => `${Number(v || 0).toFixed(2)}%`,
    },
    {
      key: "cpm",
      header: "CPM",
      align: "right",
      visible: false,
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "connectRate",
      header: "Connect Rate",
      align: "right",
      visible: false,
      render: (v) => `${Number(v || 0).toFixed(2)}%`,
    },
  ];
}

// Colunas para a tabela de Nome do Anúncio (ads)
function buildColunasAds(_data: MetaAdsAdPerformanceItem[]): TableColumn[] {
  return [
    {
      key: "ad_name",
      header: "Anúncio",
      align: "left",
      render: (_v, row, _idx, isTotal) => {
        const name = row?.ad_name || "";
        if (isTotal)
          return <span className="font-semibold">{String(name)}</span>;
        const thumb = row?.thumbnail_url;
        return (
          <div className="flex items-center gap-3 min-w-[200px]">
            {thumb ? (
              <img
                src={String(thumb)}
                alt=""
                className="w-10 h-10 object-cover rounded flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <div className="w-10 h-10 bg-gray-700/50 rounded flex-shrink-0 flex items-center justify-center">
                <ImageOff className="w-5 h-5 text-gray-400" />
              </div>
            )}
            <span className="truncate text-sm" title={String(name)}>
              {String(name)}
            </span>
          </div>
        );
      },
    },
    {
      key: "pagDestino",
      header: "Pág. Destino",
      align: "right",
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "custoPagDestino",
      header: "Custo Pág. Destino",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right",
      render: (v) => formatPercentCell(v),
    },
    {
      key: "investimento",
      header: "Investimento",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "compras",
      header: "Compras",
      align: "right",
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "receita",
      header: "Receita",
      align: "right",
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "roas",
      header: "ROAS",
      align: "right",
      render: (v) => formatRoas(Number(v) || 0),
    },
    {
      key: "delta",
      header: "% Δ",
      align: "right",
      render: (v) => renderDeltaPercentCell(v),
    },
    // --- Engrenagem ---
    {
      key: "alcance",
      header: "Alcance",
      align: "right",
      visible: false,
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "frequencia",
      header: "Frequência",
      align: "right",
      visible: false,
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      key: "cpcMedio",
      header: "CPC",
      align: "right",
      visible: false,
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "cliquesLink",
      header: "Cliques Link",
      align: "right",
      visible: false,
      render: (v) => formatNumber(Number(v) || 0),
    },
    {
      key: "ctr",
      header: "CTR",
      align: "right",
      visible: false,
      render: (v) => formatPercentCell(v),
    },
    {
      key: "ctrTodos",
      header: "CTR Todos",
      align: "right",
      visible: false,
      render: (v) => `${Number(v || 0).toFixed(2)}%`,
    },
    {
      key: "cpm",
      header: "CPM",
      align: "right",
      visible: false,
      render: (v) => formatCurrency(Number(v) || 0),
    },
    {
      key: "connectRate",
      header: "Connect Rate",
      align: "right",
      visible: false,
      render: (v) => `${Number(v || 0).toFixed(2)}%`,
    },
  ];
}

// Colunas Google Ads: Produtos + Termos de pesquisa (mantidas)
const colunasGoogle = {
  products: [
    { key: "produto", header: "Produtos (Shopping)", align: "left" as const },
    {
      key: "cpcMedio",
      header: "CPC médio",
      align: "right" as const,
      render: (v: unknown) =>
        v === null || v === undefined ? "N/D" : formatCurrency(Number(v) || 0),
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right" as const,
      render: (v: unknown) => formatPercentCell(v),
    },
    {
      key: "cpa",
      header: "CPA",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "investimento",
      header: "Investimento",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "compras",
      header: "Compras",
      align: "right" as const,
      render: (v: unknown) => formatNumber(Number(v) || 0),
    },
    {
      key: "valorCompras",
      header: "Valor de todas as compras",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "delta",
      header: "% Δ",
      align: "right" as const,
      render: (v: unknown) => renderDeltaPercentCell(v),
    },
    {
      key: "roas",
      header: "ROAS",
      align: "right" as const,
      render: (v: unknown) => formatRoas(Number(v) || 0),
    },
    {
      key: "deltaRoas",
      header: "% Δ",
      align: "right" as const,
      render: (v: unknown) => renderDeltaPercentCell(v),
    },
  ] as TableColumn[],
  searchTerms: [
    { key: "termo", header: "Termo de pesquisa", align: "left" as const },
    {
      key: "cpcMedio",
      header: "CPC médio",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "txConversao",
      header: "TX Conversão",
      align: "right" as const,
      render: (v: unknown) => formatPercentCell(v),
    },
    {
      key: "cpa",
      header: "CPA",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "investimento",
      header: "Investimento",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "compras",
      header: "Compras",
      align: "right" as const,
      render: (v: unknown) => formatNumber(Number(v) || 0),
    },
    {
      key: "receita",
      header: "Receita",
      align: "right" as const,
      render: (v: unknown) => formatCurrency(Number(v) || 0),
    },
    {
      key: "deltaReceita",
      header: "% Δ",
      align: "right" as const,
      render: (v: unknown) => renderDeltaPercentCell(v),
    },
    {
      key: "roas",
      header: "ROAS",
      align: "right" as const,
      render: (v: unknown) => formatRoas(Number(v) || 0),
    },
    {
      key: "deltaRoas",
      header: "% Δ",
      align: "right" as const,
      render: (v: unknown) => renderDeltaPercentCell(v),
    },
  ] as TableColumn[],
};

// Componente para indicador de tendência
const TrendIndicator = ({
  value,
  trend,
}: {
  value: number;
  trend: "up" | "down" | "neutral";
}) => {
  const { filter } = useGlobalFilter();
  if (!isComparisonEnabled(filter)) return null;

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      {trend !== "neutral" &&
        (trend === "up" ? (
          <TrendingUp className="w-3 h-3 mr-1 text-green-500" />
        ) : (
          <TrendingDown className="w-3 h-3 mr-1 text-red-500" />
        ))}
      <span
        className={
          trend === "up"
            ? "text-green-500"
            : trend === "down"
              ? "text-red-500"
              : "text-muted-foreground"
        }
      >
        {formatSignedPercent(Number(value || 0))}
      </span>
    </div>
  );
};

type AdsPlatform = "google" | "meta";

const KPI_METRICS = [
  "receita",
  "investimento",
  "roas",
  "compras",
  "ticket-medio",
  "taxa-conversao",
  "cpa",
  "cps",
  "cpm",
  "impressoes",
  "ctr",
  "cliques",
  "cpc",
  "sessoes",
] as const;

const CHART_SLUGS = [
  "performance",
  "investment-vs-receita",
  "investment-vs-cpc",
  "investment-vs-roas",
  "cps-vs-sessoes",
  "taxa-conversao-vs-pedidos",
  "ticket-medio-vs-roas",
] as const;

const TABLE_SLUGS = ["campaigns", "products", "search-terms"] as const;

/**
 * As tabelas que cada aba **desenha** — e, por isso, as únicas que ela busca.
 *
 * <M-224> Antes, as três eram buscadas nas duas plataformas. Na aba Meta isso significava
 * duas chamadas lentas ao Graph cujo resultado nenhum componente lia — e, pior, um resultado
 * que ficava guardado em `tablesData`: ao trocar para a aba Google, as tabelas "Produtos
 * (Shopping)" e "Termos de Pesquisa" apareciam **preenchidas com a resposta do Meta**, sob
 * cabeçalhos do Google, até a busca nova terminar. É o item 7 da auditoria da Magnata (com o
 * 8 e o 9), e a leitura pela tela o diagnosticou como "o bloco lê o dataset errado"; a rota
 * sempre leu o dataset certo — quem trocava era o estado da página.
 */
const TABLE_SLUGS_POR_PLATAFORMA = {
  meta: ["campaigns"],
  google: ["campaigns", "products", "search-terms"],
} as const satisfies Record<AdsPlatform, readonly TableSlug[]>;

type AdsAccount = GoogleAdsAccountInfo | MetaAdsAccount;
type ChartTooltipEntry = NonNullable<
  TooltipProps<ValueType, NameType>["payload"]
>[number];

type KpiMetricState = {
  value: number;
  change: number;
  trend: "up" | "down" | "neutral";
  unit?: string;
};

type ChartPoint = {
  date?: string;
  [key: string]: string | number | null | undefined;
};

type TablePaginationState = {
  current_page?: number;
  current?: number;
  total_items?: number;
  total?: number;
  page_size?: number;
  pageSize?: number;
};

type TableState = {
  items: (CampaignTableItem | ProductTableItem | SearchTermTableItem)[];
  pagination?: TablePaginationState;
  /**
   * O "Total Geral" publicado pela rota, quando ela publica um.
   *
   * <M-220> `campaigns` foi a primeira; <M-224> `products` e `search-terms` acompanharam,
   * porque as duas passaram a ser vistas com dado do Google e chegariam com o rodapé somado
   * que a própria tabela monta. O campo continua opcional: `undefined` devolve a tabela a
   * esse rodapé automático.
   */
  totalRow?: CampaignTableItem | ProductTableItem | SearchTermTableItem | null;
};

type TableSlug = (typeof TABLE_SLUGS)[number];

const DEFAULT_PAGE_SIZE = 10;

const getAdsDashboardTrend = (
  trend: string | undefined,
): KpiMetricState["trend"] => {
  return trend === "up" || trend === "down" || trend === "neutral"
    ? trend
    : "neutral";
};

const getTooltipKey = (entry: ChartTooltipEntry) => String(entry.dataKey ?? "");

const getTooltipNumber = (entry: ChartTooltipEntry) => Number(entry.value) || 0;

const createEmptyTableState = (): TableState => ({
  items: [],
  pagination: {
    current_page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    total_items: 0,
  },
});

const createEmptyTablesState = (): Record<TableSlug, TableState> => ({
  campaigns: createEmptyTableState(),
  products: createEmptyTableState(),
  "search-terms": createEmptyTableState(),
});

const ADSPage: React.FC = () => {
  const { filter: filterState } = useGlobalFilter();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [selectedPlatform, setSelectedPlatform] = useState<AdsPlatform>("meta");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<AdsAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [kpiData, setKpiData] = useState<Record<string, KpiMetricState>>({});
  const [chartsData, setChartsData] = useState<Record<string, ChartPoint[]>>(
    {},
  );
  const [tablesData, setTablesData] = useState<Record<TableSlug, TableState>>(
    createEmptyTablesState(),
  );
  // Começa ligado quando há loja: com `false`, a primeira pintura saía antes do efeito e os
  // quatorze cards desenhavam "R$ 0,00". Sem loja o efeito nem busca, e ligado ficaria para sempre.
  const [kpisLoading, setKpisLoading] = useState(() =>
    Boolean(localStorage.getItem("active_customer_uuid")),
  );
  const [erroKpis, setErroKpis] = useState(false);
  // Mesma razão para gráficos e tabelas: com `false`, a primeira pintura mostrava as tabelas
  // dizendo "Nenhum dado encontrado" antes de a busca sair.
  const [chartsLoading, setChartsLoading] = useState(() =>
    Boolean(localStorage.getItem("active_customer_uuid")),
  );
  const [tablesLoading, setTablesLoading] = useState(() =>
    Boolean(localStorage.getItem("active_customer_uuid")),
  );
  // Ref para evitar race condition: respostas de chamadas antigas são descartadas
  const fetchIdRef = React.useRef(0);

  /**
   * Troca a aba — e esquece, no mesmo instante, tudo o que era da aba anterior.
   *
   * <M-224> **Este é o conserto dos itens 7, 8 e 9 da auditoria da Magnata.** Trocar de
   * plataforma trocava o título, as colunas e as tabelas visíveis na mesma renderização, e
   * deixava `kpiData`, `chartsData` e `tablesData` com o conteúdo da plataforma anterior até
   * a busca nova responder. Como `fetchDashboardData` busca KPIs, depois gráficos e só então
   * tabelas — cada etapa esperando a anterior —, os cartões do topo já traziam os números do
   * Google enquanto as três tabelas ainda mostravam os do Meta. Foi exatamente essa
   * composição que a auditoria fotografou: *"os KPIs do topo estão certos, logo o componente
   * de tabela é que está vinculado à fonte errada"*.
   *
   * A janela não era curta: as leituras do Google Ads levam segundos, e "Termos de Pesquisa"
   * chegava a mostrar **nomes de anúncios do Meta**, porque naquela plataforma o slug
   * `search-terms` devolve anúncios, e não termos.
   *
   * `selectedAccount` vai junto: ele guarda um id da outra plataforma, e a primeira busca
   * sairia recortada por uma conta que não existe do lado de cá — o servidor não encontra a
   * conta e responde uma tabela vazia. Zerado, o recorte é "todas as contas desta
   * plataforma", que é o que `loadPlatformData` vai estreitar em seguida.
   */
  const trocarDePlataforma = (plataforma: AdsPlatform) => {
    if (plataforma === selectedPlatform) return;
    setSelectedPlatform(plataforma);
    setAccounts([]);
    setSelectedAccount("");
    setKpiData({});
    setChartsData({});
    setTablesData(createEmptyTablesState());
    setKpisLoading(true);
    setChartsLoading(true);
    setTablesLoading(true);
    // <M-224> Achado do code review: `adsetsData`/`adNamesData` são o mesmo tipo de estado
    // preso à plataforma que `tablesData` — CONJUNTO DE ANÚNCIOS e NOME DO ANÚNCIO só
    // aparecem na aba Meta, mas ficavam de fora do reset. `StandardizedTable` só **escurece**
    // a linha existente enquanto `loading` está ligado (não a esconde), então ir para o
    // Google e voltar para o Meta desenhava, por baixo do véu, os anúncios da conta ou do
    // período de antes da viagem — até `fetchAdsets`/`fetchAdNames` responderem de novo.
    setAdsetsData([]);
    setAdsetsTotal(undefined);
    setAdNamesData([]);
    setAdNamesTotal(undefined);
  };
  const getKpiValue = (metric: string) => kpiData[metric]?.value ?? 0;
  const getKpiChange = (metric: string) => kpiData[metric]?.change ?? 0;
  const getKpiTrend = (metric: string) =>
    (kpiData[metric]?.trend ?? "neutral") as "up" | "down" | "neutral";
  // Métrica que não veio é N/D, e não o zero de `getKpiValue`.
  const kpiDisponivel = (metric: string) =>
    Number.isFinite(kpiData[metric]?.value);
  /** A mídia da aba; CPS e Sessões dividem pelas sessões do GA4, e sem ele saem zeradas. */
  const fontesDoKpi = (metric: string): readonly RequisitoDeFonte[] => {
    const midia: FonteDeDados =
      selectedPlatform === "google" ? "google_ads" : "meta_ads";
    return metric === "cps" || metric === "sessoes" ? [midia, "ga4"] : [midia];
  };
  const getChartData = (slug: string) => {
    const data = chartsData[slug] || [];
    return data.map((item: ChartPoint) => ({
      ...item,
      date: item.date ? formatDateBR(item.date) : item.date,
    }));
  };
  const getTableItems = (slug: TableSlug) => tablesData[slug]?.items || [];
  /**
   * O "Total Geral" que a rota publica, ou `undefined` quando ela não publica nenhum.
   *
   * <M-220> A tabela CAMPANHA desenhava um rodapé que ela mesma montava: para cada coluna,
   * somava se o valor parecesse número e tirava a média se a coluna estivesse declarada como
   * percentual. Nas colunas do Meta isso dava, na conta da auditoria, ROAS 17,81 (a soma dos
   * seis ROAS), TX 3,56 % (a soma das seis taxas — a coluna não declara `type`, e a heurística
   * não infere `percent` sozinha) e custo por página de destino 8,00, contra 3,83, 0,53 % e
   * 1,20 dos agregados.
   *
   * O rodapé agora vem pronto do servidor, e vem pelo `total` — e não pelo `computeTotal` que
   * M-219 estreou na ANÁLISE MÍDIA PAGA. A diferença é qual conjunto cada um enxerga: lá as
   * duas linhas do card estão todas na tela, e o rodapé tem de acompanhar a busca da própria
   * tabela; aqui a tabela recebe **uma página** de um conjunto que o cliente não tem, e um
   * rodapé calculado sobre ela seria o padrão B da auditoria. O total do conjunto só pode vir
   * de quem tem o conjunto.
   *
   * `undefined` devolve a tabela ao rodapé automático, que é o que as outras duas tabelas
   * desta tela continuam usando.
   */
  const getTableTotal = (slug: TableSlug): TableTotal | undefined => {
    const linha = tablesData[slug]?.totalRow;
    return linha ? { ...linha } : undefined;
  };
  const getTablePagination = (slug: TableSlug) => {
    const pagination = tablesData[slug]?.pagination;
    const current = pagination?.current_page ?? pagination?.current ?? 1;
    const total =
      pagination?.total_items ??
      pagination?.total ??
      tablesData[slug]?.items.length ??
      0;
    const pageSize =
      pagination?.page_size ?? pagination?.pageSize ?? DEFAULT_PAGE_SIZE;
    return {
      current,
      total,
      pageSize,
    };
  };

  const handleFilterChange = (filter: FilterState) => {
    console.log("Filtro aplicado:", filter);
  };

  // ─── Table pagination ──────────────────────────────────────────────────────
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [productsPage, setProductsPage] = useState(1);
  const [searchTermsPage, setSearchTermsPage] = useState(1);

  const fetchCampaignsPage = useCallback(
    async (page: number) => {
      const accountIdParam = selectedAccount || undefined;
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      const comparison =
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period";
      const customerUuid = localStorage.getItem("active_customer_uuid");
      if (!startDate || !endDate || !customerUuid) return;
      // <M-224> A mesma proteção de `fetchDashboardData`, achada pelo code review: sem ela,
      // um clique de página desta tabela que ainda está em voo quando o usuário troca de
      // plataforma (ou de conta, ou o filtro de datas) sobrescreveria `tablesData.campaigns`
      // com a resposta de um pedido que já não corresponde à tela — e limparia
      // `tablesLoading` embaixo do carregamento novo que `trocarDePlataforma` acabou de
      // ligar. `fetchIdRef` é o mesmo contador que `fetchDashboardData` avança a cada troca.
      const chamadaId = fetchIdRef.current;
      const desatualizada = () => fetchIdRef.current !== chamadaId;
      setTablesLoading(true);
      try {
        const { data } =
          await proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet({
            path: { platform: selectedPlatform, table: "campaigns" },
            query: {
              customer_uuid: customerUuid,
              start_date: startDate,
              end_date: endDate,
              comparison,
              account_id: accountIdParam,
              page,
              page_size: DEFAULT_PAGE_SIZE,
            },
            throwOnError: true,
          });
        if (desatualizada()) return;
        setTablesData((prev) => ({
          ...prev,
          campaigns: {
            items:
              (data as AdsDashboardTableResponseCampaignTableItem).items ?? [],
            pagination: data.pagination,
            // <M-220> Vem da rota a cada página, e é sempre o do conjunto inteiro — trocar de
            // página não troca o "Total Geral".
            totalRow: (data as AdsDashboardTableResponseCampaignTableItem)
              .total_row,
          },
        }));
      } catch (error) {
        if (desatualizada()) return;
        console.error("Erro ao carregar campanhas", error);
      } finally {
        if (!desatualizada()) setTablesLoading(false);
      }
    },
    [filterState, selectedAccount, selectedPlatform],
  );

  const fetchProductsPage = useCallback(
    async (page: number) => {
      const accountIdParam = selectedAccount || undefined;
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      const comparison =
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period";
      const customerUuid = localStorage.getItem("active_customer_uuid");
      if (!startDate || !endDate || !customerUuid) return;
      // <M-224> Ver o comentário equivalente em `fetchCampaignsPage` — a mesma corrida, com a
      // mesma consequência: sem a proteção, um clique de página que ainda está em voo quando
      // a plataforma troca sobrescreveria a tabela de Produtos com a resposta de um pedido já
      // descartado.
      const chamadaId = fetchIdRef.current;
      const desatualizada = () => fetchIdRef.current !== chamadaId;
      setTablesLoading(true);
      try {
        const { data } =
          await proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet({
            path: { platform: selectedPlatform, table: "products" },
            query: {
              customer_uuid: customerUuid,
              start_date: startDate,
              end_date: endDate,
              comparison,
              account_id: accountIdParam,
              page,
              page_size: DEFAULT_PAGE_SIZE,
            },
            throwOnError: true,
          });
        if (desatualizada()) return;
        setTablesData((prev) => ({
          ...prev,
          products: {
            items:
              (data as AdsDashboardTableResponseProductTableItem).items ?? [],
            pagination: data.pagination,
            // <M-224> Vem da rota a cada página, e é sempre o do conjunto inteiro — trocar de
            // página não troca o "Total Geral".
            totalRow: (data as AdsDashboardTableResponseProductTableItem)
              .total_row,
          },
        }));
      } catch (error) {
        if (desatualizada()) return;
        console.error("Erro ao carregar produtos", error);
      } finally {
        if (!desatualizada()) setTablesLoading(false);
      }
    },
    [filterState, selectedAccount, selectedPlatform],
  );

  const fetchSearchTermsPage = useCallback(
    async (page: number) => {
      const accountIdParam = selectedAccount || undefined;
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      const comparison =
        filterState.comparison?.type === "previousYear"
          ? "previous_year"
          : "previous_period";
      const customerUuid = localStorage.getItem("active_customer_uuid");
      if (!startDate || !endDate || !customerUuid) return;
      // <M-224> Ver o comentário equivalente em `fetchCampaignsPage`.
      const chamadaId = fetchIdRef.current;
      const desatualizada = () => fetchIdRef.current !== chamadaId;
      setTablesLoading(true);
      try {
        const { data } =
          await proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet({
            path: { platform: selectedPlatform, table: "search-terms" },
            query: {
              customer_uuid: customerUuid,
              start_date: startDate,
              end_date: endDate,
              comparison,
              account_id: accountIdParam,
              page,
              page_size: DEFAULT_PAGE_SIZE,
            },
            throwOnError: true,
          });
        if (desatualizada()) return;
        setTablesData((prev) => ({
          ...prev,
          "search-terms": {
            items:
              (data as AdsDashboardTableResponseSearchTermTableItem).items ??
              [],
            pagination: data.pagination,
            // <M-224> Como em `products`: o rodapé é o do conjunto, e vem em toda página.
            totalRow: (data as AdsDashboardTableResponseSearchTermTableItem)
              .total_row,
          },
        }));
      } catch (error) {
        if (desatualizada()) return;
        console.error("Erro ao carregar termos de pesquisa", error);
      } finally {
        if (!desatualizada()) setTablesLoading(false);
      }
    },
    [filterState, selectedAccount, selectedPlatform],
  );

  // ─── Adsets & Ads (Meta only) ───────────────────────────────────────────────
  const [adsetsData, setAdsetsData] = useState<MetaAdsFeaturedAdsetItem[]>([]);
  // Nasce ligado: a página abre em Meta, e o efeito de baixo busca os conjuntos na montagem.
  const [adsetsLoading, setAdsetsLoading] = useState(true);
  /**
   * O "Total Geral" que `/featured-adsets` publica, ou `undefined` enquanto não veio.
   *
   * <M-222> Mesmo defeito e mesmo conserto da tabela de baixo. Esta não aparece na auditoria
   * porque, quando ela foi lida, `receita` e `roas` eram a constante zero e o rodapé somava
   * zeros; desde que M-221 deu receita à tabela, um rodapé somado mostraria um ROAS inventado.
   *
   * O tipo é `…TotalRow`, e não `…Item`: `alcance`, `reach` e `frequencia` **não vêm** no
   * total, porque alcance não é aditivo — o Graph deduplica dentro de cada conjunto, e quem
   * dois conjuntos atingiram entraria duas vezes. A tabela desenha "N/D" onde não há valor.
   */
  const [adsetsTotal, setAdsetsTotal] = useState<
    MetaAdsFeaturedAdsetTotalRow | undefined
  >(undefined);
  const [adsetsPag, setAdsetsPag] = useState({
    current_page: 1,
    total_results: 0,
    total_pages: 0,
  });
  const [, setAdsetsPage] = useState(1);
  const [adNamesData, setAdNamesData] = useState<MetaAdsAdPerformanceItem[]>(
    [],
  );
  /**
   * O "Total Geral" que `/ad-performance` publica, ou `undefined` enquanto não veio.
   *
   * <M-222> A tabela NOME DO ANÚNCIO desenhava um rodapé que ela mesma montava, somando cada
   * coluna numérica das linhas que tinha em mãos. Somar ROAS não dá ROAS: na conta da
   * auditoria dava **116,18**, contra 3,83 do conjunto. E as linhas que ela tem em mãos são as
   * de **uma página** de vinte, de um conjunto que o cliente não tem — o mesmo par de defeitos
   * que M-220 tirou da tabela CAMPANHA, pela mesma via.
   *
   * O tipo é `…TotalRow`, e não `…Item`: `reach` não vem no total, pela mesma razão de
   * `adsetsTotal`.
   */
  const [adNamesTotal, setAdNamesTotal] = useState<
    MetaAdsAdPerformanceTotalRow | undefined
  >(undefined);
  const [adNamesLoading, setAdNamesLoading] = useState(true);
  const [adNamesPag, setAdNamesPag] = useState({
    current_page: 1,
    total_results: 0,
    total_pages: 0,
  });
  const [, setAdNamesPage] = useState(1);
  const [adNameSearch, setAdNameSearch] = useState("");

  const resolveAccountId = (account: AdsAccount) =>
    "account_id" in account ? account.account_id : account.id;

  const loadPlatformData = useCallback(async () => {
    try {
      setLoading(true);
      const accountsData =
        selectedPlatform === "google"
          ? ((await getGoogleAdsAccountsApiV1IntegrationsGoogleAdsAccountsGet())
              .data ?? [])
          : ((await getAccountsApiV1IntegrationsMetaAdsAccountsGet()).data ??
            []);
      setAccounts(accountsData);

      if (accountsData.length > 0) {
        const firstAccount = accountsData[0];
        setSelectedAccount(resolveAccountId(firstAccount));
      } else {
        setSelectedAccount("");
      }
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
      toast({
        title: "Erro ao carregar contas",
        description: String(error),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [selectedPlatform, toast]);

  const fetchDashboardData = useCallback(async () => {
    const customerUuid = localStorage.getItem("active_customer_uuid");
    if (!customerUuid) {
      console.warn("Nenhum customer_uuid definido");
      return;
    }

    // Incrementar ID para detectar chamadas stale (race condition)
    const currentFetchId = ++fetchIdRef.current;
    const isStale = () => fetchIdRef.current !== currentFetchId;

    const startDate = formatDateToISO(filterState.dateRange.startDate);
    const endDate = formatDateToISO(filterState.dateRange.endDate);
    const comparison =
      filterState.comparison?.type === "previousYear"
        ? "previous_year"
        : "previous_period";
    const accountIdParam = selectedAccount || undefined;

    const baseParams = {
      customer_uuid: customerUuid,
      start_date: startDate,
      end_date: endDate,
      comparison,
      account_id: accountIdParam,
    };

    // <M-224> As três etapas são sequenciais, e cada uma sinalizava o seu carregamento só
    // quando chegava a vez dela. Enquanto os KPIs e os gráficos eram buscados, as tabelas
    // ficavam sem indicador nenhum — desenhando o que sobrou da busca anterior como se fosse
    // o resultado desta. Marcar as três aqui é o que faz a tela dizer "carregando" durante a
    // troca inteira.
    setKpisLoading(true);
    setChartsLoading(true);
    setTablesLoading(true);

    try {
      const kpiResponses = await Promise.all(
        KPI_METRICS.map(async (metric) => {
          const { data } =
            await proxyAdsDashboardKpiApiV1AdsDashboardPlatformKpisMetricGet({
              path: { platform: selectedPlatform, metric },
              query: baseParams,
              throwOnError: true,
            });
          return [metric, data] as const;
        }),
      );
      if (isStale()) return;
      const mappedKpis = kpiResponses.reduce<Record<string, KpiMetricState>>(
        (acc, [metric, data]) => {
          acc[metric] = {
            value: data.value,
            change: data.change_percentage ?? 0,
            trend: getAdsDashboardTrend(data.trend),
            unit: data.unit,
          };
          return acc;
        },
        {},
      );
      setKpiData(mappedKpis);
      setErroKpis(false);
    } catch (error) {
      if (isStale()) return;
      console.error("Erro ao carregar KPIs", error);
      toast({
        title: "Erro ao carregar KPIs",
        description: String(error),
        variant: "destructive",
      });
      setKpiData({});
      setErroKpis(true);
    } finally {
      if (!isStale()) setKpisLoading(false);
    }

    if (isStale()) return;

    try {
      const chartResponses = await Promise.all(
        CHART_SLUGS.map(async (slug) => {
          const chartParams = {
            customer_uuid: customerUuid,
            start_date: startDate,
            end_date: endDate,
            account_id: accountIdParam,
          };
          const { data } =
            await proxyAdsDashboardChartApiV1AdsDashboardPlatformChartsChartGet(
              {
                path: { platform: selectedPlatform, chart: slug },
                query: chartParams,
                throwOnError: true,
              },
            );
          return [slug, data.data ?? []] as const;
        }),
      );
      if (isStale()) return;
      setChartsData(
        chartResponses.reduce<Record<string, ChartPoint[]>>(
          (acc, [slug, data]) => {
            acc[slug] = data;
            return acc;
          },
          {},
        ),
      );
    } catch (error) {
      if (isStale()) return;
      console.error("Erro ao carregar gráficos", error);
      toast({
        title: "Erro ao carregar gráficos",
        description: String(error),
        variant: "destructive",
      });
      setChartsData({});
    } finally {
      if (!isStale()) setChartsLoading(false);
    }

    if (isStale()) return;

    try {
      const tableResponses = await Promise.all(
        TABLE_SLUGS_POR_PLATAFORMA[selectedPlatform].map(async (slug) => {
          const { data } =
            await proxyAdsDashboardTableApiV1AdsDashboardPlatformTablesTableGet(
              {
                path: { platform: selectedPlatform, table: slug },
                query: { ...baseParams, page: 1, page_size: DEFAULT_PAGE_SIZE },
                throwOnError: true,
              },
            );
          const typed = data as
            | AdsDashboardTableResponseCampaignTableItem
            | AdsDashboardTableResponseProductTableItem
            | AdsDashboardTableResponseSearchTermTableItem;
          return [slug, typed] as const;
        }),
      );
      if (isStale()) return;
      const nextTables = createEmptyTablesState();
      setTablesData(
        tableResponses.reduce<Record<TableSlug, TableState>>(
          (acc, [slug, data]) => {
            acc[slug] = {
              items: data.items ?? [],
              pagination: data.pagination,
              totalRow: "total_row" in data ? data.total_row : undefined,
            };
            return acc;
          },
          nextTables,
        ),
      );
    } catch (error) {
      if (isStale()) return;
      console.error("Erro ao carregar tabelas", error);
      toast({
        title: "Erro ao carregar tabelas",
        description: String(error),
        variant: "destructive",
      });
      setTablesData(createEmptyTablesState());
    } finally {
      if (!isStale()) setTablesLoading(false);
    }
  }, [filterState, selectedAccount, selectedPlatform, toast]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadPlatformData();
  }, [loadPlatformData]);

  useEffect(() => {
    const customerUuid = localStorage.getItem("active_customer_uuid");
    if (!customerUuid) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ─── Fetch Adsets & Ads (Meta only) ─────────────────────────────────────────
  const fetchAdsets = useCallback(
    async (page = 1) => {
      if (selectedPlatform !== "meta") return;
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      setAdsetsLoading(true);
      try {
        const { data } =
          await getMetaAdsFeaturedAdsetsApiV1IntegrationsMetaAdsFeaturedAdsetsGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                page,
                page_size: 20,
              },
              throwOnError: true,
            },
          );
        setAdsetsData(data.results || []);
        // <M-222> Vem a cada página, e é sempre o do conjunto inteiro: trocar de página não
        // troca o "Total Geral". `null` (conjunto vazio) devolve a tabela ao rodapé próprio.
        setAdsetsTotal(data.total_row ?? undefined);
        setAdsetsPag({
          current_page: data.current_page || 1,
          total_results: data.total_results || 0,
          total_pages: data.total_pages || 0,
        });
      } catch (err) {
        console.error("Erro ao carregar adsets:", err);
        setAdsetsData([]);
        setAdsetsTotal(undefined);
      } finally {
        setAdsetsLoading(false);
      }
    },
    [selectedPlatform, filterState],
  );

  const fetchAdNames = useCallback(
    async (page = 1) => {
      if (selectedPlatform !== "meta") return;
      const startDate = formatDateToISO(filterState.dateRange.startDate);
      const endDate = formatDateToISO(filterState.dateRange.endDate);
      setAdNamesLoading(true);
      try {
        const { data } =
          await getMetaAdsAdPerformanceApiV1IntegrationsMetaAdsAdPerformanceGet(
            {
              query: {
                start_date: startDate,
                end_date: endDate,
                page,
                page_size: 20,
                sort_by: "spend",
                sort_order: "desc",
                ...(adNameSearch ? { q: adNameSearch } : {}),
              },
              throwOnError: true,
            },
          );
        setAdNamesData(data.results || []);
        // <M-222> Idem, e este respeita o `q` da caixa de busca: o total é o do conjunto
        // filtrado, não o da conta inteira.
        setAdNamesTotal(data.total_row ?? undefined);
        setAdNamesPag({
          current_page: data.current_page || 1,
          total_results: data.total_results || 0,
          total_pages: data.total_pages || 0,
        });
      } catch (err) {
        console.error("Erro ao carregar ads:", err);
        setAdNamesData([]);
        setAdNamesTotal(undefined);
      } finally {
        setAdNamesLoading(false);
      }
    },
    [selectedPlatform, filterState, adNameSearch],
  );

  useEffect(() => {
    if (selectedPlatform === "meta") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      fetchAdsets(1);
      fetchAdNames(1);
    }
  }, [fetchAdsets, fetchAdNames, selectedPlatform]);

  return (
    <div className="p-6 space-y-6 page-background">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-4 mb-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {selectedPlatform === "google" ? "Google Ads" : "Meta Ads"}
            </h1>

            {/* Seletor de Plataforma */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <Button
                size="sm"
                variant={selectedPlatform === "meta" ? "default" : "ghost"}
                onClick={() => trocarDePlataforma("meta")}
                className="gap-2"
              >
                <Activity className="h-4 w-4" />
                Meta Ads
              </Button>
              <Button
                size="sm"
                variant={selectedPlatform === "google" ? "default" : "ghost"}
                onClick={() => trocarDePlataforma("google")}
                className="gap-2"
              >
                <Search className="h-4 w-4" />
                Google Ads
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            {selectedPlatform === "google"
              ? "Dashboard de performance de campanhas publicitárias no Google Ads"
              : "Dashboard de performance de campanhas publicitárias no Facebook e Instagram"}
          </p>
        </div>

        {/* Badge de Status */}
        <Badge
          variant="outline"
          className={
            loading ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"
          }
        >
          {loading ? "Carregando..." : "Pronto"}
        </Badge>
      </div>

      <GlobalFilter />

      {/* Botão Melhores Resultados Meta */}
      {selectedPlatform === "meta" && (
        <div>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 text-sm font-medium"
            onClick={() => navigate("/dashboard/melhores-resultados-meta")}
          >
            <Target className="h-4 w-4" />
            MELHORES RESULTADOS
          </Button>
        </div>
      )}

      <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
        {/* Receita */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Receita</span>
                <HelpTooltip
                  content={getKpiTooltip("Receita")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("receita")}
                  fontes={fontesDoKpi("receita")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("receita")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Receita"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("receita") && (
                <TrendIndicator
                  value={getKpiChange("receita")}
                  trend={getKpiTrend("receita")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Investimento */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Investimento</span>
                <HelpTooltip
                  content={getKpiTooltip("Investimento")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("investimento")}
                  fontes={fontesDoKpi("investimento")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("investimento")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Investimento"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("investimento") && (
                <TrendIndicator
                  value={getKpiChange("investimento")}
                  trend={getKpiTrend("investimento")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* ROAS */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">ROAS</span>
                <HelpTooltip
                  content={getKpiTooltip("ROAS")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("roas")}
                  fontes={fontesDoKpi("roas")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("roas")}
                    format={(n) => n.toFixed(2)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="ROAS"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("roas") && (
                <TrendIndicator
                  value={getKpiChange("roas")}
                  trend={getKpiTrend("roas")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compras */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Compras</span>
                <HelpTooltip
                  content={getKpiTooltip("Compras")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("compras")}
                  fontes={fontesDoKpi("compras")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("compras")}
                    format={(n) => formatNumber(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Compras"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("compras") && (
                <TrendIndicator
                  value={getKpiChange("compras")}
                  trend={getKpiTrend("compras")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ticket Médio */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Ticket Médio</span>
                <HelpTooltip
                  content={getKpiTooltip("Ticket Médio")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("ticket-medio")}
                  fontes={fontesDoKpi("ticket-medio")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("ticket-medio")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Ticket médio"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("ticket-medio") && (
                <TrendIndicator
                  value={getKpiChange("ticket-medio")}
                  trend={getKpiTrend("ticket-medio")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Taxa Conversão */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Taxa Conversão</span>
                <HelpTooltip
                  content={getKpiTooltip("Taxa Conversão")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("taxa-conversao")}
                  fontes={fontesDoKpi("taxa-conversao")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("taxa-conversao")}
                    format={(n) => `${n.toFixed(2)}%`}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Taxa de conversão"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("taxa-conversao") && (
                <TrendIndicator
                  value={getKpiChange("taxa-conversao")}
                  trend={getKpiTrend("taxa-conversao")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* CPA */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">CPA</span>
                <HelpTooltip
                  content={getKpiTooltip("CPA")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("cpa")}
                  fontes={fontesDoKpi("cpa")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("cpa")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="CPA"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("cpa") && (
                <TrendIndicator
                  value={getKpiChange("cpa")}
                  trend={getKpiTrend("cpa")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* CPS (GA) */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">CPS (GA)</span>
                <HelpTooltip
                  content={getKpiTooltip("CPS (GA)")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("cps")}
                  fontes={fontesDoKpi("cps")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("cps")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="CPS"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("cps") && (
                <TrendIndicator
                  value={getKpiChange("cps")}
                  trend={getKpiTrend("cps")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* CPM */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">CPM</span>
                <HelpTooltip
                  content={getKpiTooltip("CPM")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("cpm")}
                  fontes={fontesDoKpi("cpm")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("cpm")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="CPM"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("cpm") && (
                <TrendIndicator
                  value={getKpiChange("cpm")}
                  trend={getKpiTrend("cpm")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Impressões */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Impressões</span>
                <HelpTooltip
                  content={getKpiTooltip("Impressões")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("impressoes")}
                  fontes={fontesDoKpi("impressoes")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("impressoes")}
                    format={(n) => formatNumber(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Impressões"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("impressoes") && (
                <TrendIndicator
                  value={getKpiChange("impressoes")}
                  trend={getKpiTrend("impressoes")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* CTR */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">CTR</span>
                <HelpTooltip
                  content={getKpiTooltip("CTR")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("ctr")}
                  fontes={fontesDoKpi("ctr")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("ctr")}
                    format={(n) => `${n.toFixed(2)}%`}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="CTR"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("ctr") && (
                <TrendIndicator
                  value={getKpiChange("ctr")}
                  trend={getKpiTrend("ctr")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Cliques */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Cliques</span>
                <HelpTooltip
                  content={getKpiTooltip("Cliques")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("cliques")}
                  fontes={fontesDoKpi("cliques")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("cliques")}
                    format={(n) => formatNumber(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Cliques"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("cliques") && (
                <TrendIndicator
                  value={getKpiChange("cliques")}
                  trend={getKpiTrend("cliques")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* CPC */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">CPC</span>
                <HelpTooltip
                  content={getKpiTooltip("CPC")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("cpc")}
                  fontes={fontesDoKpi("cpc")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("cpc")}
                    format={(n) => formatCurrency(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="CPC"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("cpc") && (
                <TrendIndicator
                  value={getKpiChange("cpc")}
                  trend={getKpiTrend("cpc")}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sessões (GA) */}
        <Card className="card-kpi-primary">
          <CardHeader>
            <CardTitle>
              <span className="inline-flex items-center gap-1">
                <span className="truncate">Sessões (GA)</span>
                <HelpTooltip
                  content={getKpiTooltip("Sessões (GA)")}
                  triggerClassName="h-3.5 w-3.5 text-[9px]"
                />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-start min-w-0">
              <div className="min-w-0 flex-1">
                <EstadoDoValor
                  carregando={kpisLoading}
                  disponivel={kpiDisponivel("sessoes")}
                  fontes={fontesDoKpi("sessoes")}
                  erro={erroKpis}
                  className="text-2xl font-light text-muted-foreground"
                >
                  <KpiNumber
                    value={getKpiValue("sessoes")}
                    format={(n) => formatNumber(n)}
                    className="font-light text-foreground"
                    maxFontPx={30}
                    breathingRoomPx={18}
                    ariaLabel="Sessões"
                  />
                </EstadoDoValor>
              </div>
              {!kpisLoading && kpiDisponivel("sessoes") && (
                <TrendIndicator
                  value={getKpiChange("sessoes")}
                  trend={getKpiTrend("sessoes")}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico combinado: Receita (barra) + Investimento (linha preta) + ROAS (linha amarela) */}
      {chartsLoading ? (
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-56" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-96 w-full" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Performance ao Longo do Tempo</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={384}>
              <ComposedChart
                data={getChartData("performance")}
                margin={{ top: 10, right: 50, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="left"
                  domain={["auto", "auto"]}
                  tick={{ fill: "#94a3b8", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) =>
                    v >= 1000 ? `${Math.round(v / 1000)} mil` : String(v)
                  }
                  label={{
                    value: "Investimento / Receita",
                    angle: -90,
                    position: "insideLeft",
                    fill: "#94a3b8",
                    fontSize: 11,
                    offset: -5,
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: "#f59e0b", fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  domain={["auto", "auto"]}
                  label={{
                    value: "ROAS",
                    angle: 90,
                    position: "insideRight",
                    fill: "#f59e0b",
                    fontSize: 11,
                    offset: -5,
                  }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const LABELS: Record<string, string> = {
                      investimento: "investimento",
                      receita: "receita",
                      roas: "ROAS",
                    };
                    const COLORS: Record<string, string> = {
                      investimento: "#6b7280",
                      receita: "#3b82f6",
                      roas: "#f59e0b",
                    };
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
                        <p className="text-gray-500 text-xs mb-1">{label}</p>
                        <table
                          className="border-spacing-x-2"
                          style={{ borderSpacing: "8px 2px" }}
                        >
                          <tbody>
                            {payload.map((entry: ChartTooltipEntry) => {
                              const key = getTooltipKey(entry);
                              const formatted =
                                key === "roas"
                                  ? Number(entry.value).toLocaleString(
                                      "pt-BR",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      },
                                    )
                                  : new Intl.NumberFormat("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    }).format(getTooltipNumber(entry));
                              return (
                                <tr key={key}>
                                  <td className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{
                                        backgroundColor:
                                          COLORS[key] || entry.color,
                                      }}
                                    />
                                    {LABELS[key] || key}
                                  </td>
                                  <td className="text-right font-medium text-gray-900 pl-4">
                                    {formatted}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }}
                />
                <Legend
                  wrapperStyle={{ color: "#e2e8f0", fontSize: 12 }}
                  formatter={(value: string) =>
                    value === "receita"
                      ? "Receita"
                      : value === "investimento"
                        ? "Investimento"
                        : "ROAS"
                  }
                />
                <Bar
                  yAxisId="left"
                  dataKey="receita"
                  fill="#3b82f6"
                  radius={[3, 3, 0, 0]}
                  barSize={28}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="investimento"
                  stroke="#111827"
                  strokeWidth={2.5}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="roas"
                  stroke="#f59e0b"
                  strokeWidth={2.5}
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Grid de 6 gráficos pequenos (dual Y-axis via Recharts) */}
      {chartsLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={`chart-sk-${i}`}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Gráfico 1: Investimento vs. Receita */}
          <Card>
            <CardHeader>
              <CardTitle>Investimento vs. Receita</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("investment-vs-receita")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : v.toLocaleString("pt-BR")
                    }
                    label={{
                      value: "Investimento",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : v.toLocaleString("pt-BR")
                    }
                    label={{
                      value: "Receita",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "investimento"
                                      ? "Investimento"
                                      : "Receita"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {new Intl.NumberFormat("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    }).format(getTooltipNumber(e))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "investimento" ? "Investimento" : "Receita"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="investimento"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="receita"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico 2: Investimento vs. CPC médio */}
          <Card>
            <CardHeader>
              <CardTitle>Investimento vs. CPC médio</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("investment-vs-cpc")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : v.toLocaleString("pt-BR")
                    }
                    label={{
                      value: "Investimento",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(v)
                    }
                    label={{
                      value: "CPC",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "investimento"
                                      ? "Investimento"
                                      : "CPC"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {new Intl.NumberFormat("pt-BR", {
                                      style: "currency",
                                      currency: "BRL",
                                    }).format(getTooltipNumber(e))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "investimento" ? "Investimento" : "CPC"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="investimento"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cpc"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico 3: Investimento vs. ROAS */}
          <Card>
            <CardHeader>
              <CardTitle>Investimento vs. ROAS</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("investment-vs-roas")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : v.toLocaleString("pt-BR")
                    }
                    label={{
                      value: "Investimento",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    tickFormatter={(v: number) =>
                      v.toLocaleString("pt-BR", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })
                    }
                    label={{
                      value: "ROAS",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "investimento"
                                      ? "Investimento"
                                      : "ROAS"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {e.dataKey === "roas"
                                      ? Number(e.value).toLocaleString(
                                          "pt-BR",
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          },
                                        )
                                      : new Intl.NumberFormat("pt-BR", {
                                          style: "currency",
                                          currency: "BRL",
                                        }).format(getTooltipNumber(e))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "investimento" ? "Investimento" : "ROAS"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="investimento"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="roas"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico 4: CPS vs. Sessões */}
          <Card>
            <CardHeader>
              <CardTitle>CPS vs. Sessões</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("cps-vs-sessoes")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      v >= 1000
                        ? `${(v / 1000).toLocaleString("pt-BR")}k`
                        : v.toLocaleString("pt-BR")
                    }
                    label={{
                      value: "Acessos",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(v)
                    }
                    label={{
                      value: "CPS",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "sessoes"
                                      ? "Sessões"
                                      : "CPS"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {e.dataKey === "cps"
                                      ? new Intl.NumberFormat("pt-BR", {
                                          style: "currency",
                                          currency: "BRL",
                                        }).format(getTooltipNumber(e))
                                      : getTooltipNumber(e).toLocaleString(
                                          "pt-BR",
                                        )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "sessoes" ? "Sessões" : "CPS"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="sessoes"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="cps"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico 5: Taxa Conversão vs. Pedidos */}
          <Card>
            <CardHeader>
              <CardTitle>Taxa Conversão vs. Pedidos</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("taxa-conversao-vs-pedidos")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      `${v.toLocaleString("pt-BR")}%`
                    }
                    label={{
                      value: "Taxa de Conversão",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    tickFormatter={(v: number) => v.toLocaleString("pt-BR")}
                    label={{
                      value: "Compras",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "taxaConversao"
                                      ? "Taxa de Conversão"
                                      : "Compras"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {e.dataKey === "taxaConversao"
                                      ? `${getTooltipNumber(e).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
                                      : getTooltipNumber(e).toLocaleString(
                                          "pt-BR",
                                        )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "taxaConversao" ? "Taxa de Conversão" : "Compras"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="taxaConversao"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="pedidos"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Gráfico 6: Ticket Médio vs. ROAS */}
          <Card>
            <CardHeader>
              <CardTitle>Ticket Médio vs. ROAS</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={256}>
                <ComposedChart
                  data={getChartData("ticket-medio-vs-roas")}
                  margin={{ top: 5, right: 10, left: 5, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 10,
                    }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    domain={["auto", "auto"]}
                    tick={{ fill: "#6b7280", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={55}
                    tickFormatter={(v: number) =>
                      new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(v)
                    }
                    label={{
                      value: "Ticket Médio",
                      angle: -90,
                      position: "insideLeft",
                      fill: "#6b7280",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    domain={["auto", "auto"]}
                    orientation="right"
                    tick={{ fill: "#3b82f6", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={45}
                    tickFormatter={(v: number) =>
                      v.toLocaleString("pt-BR", {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                      })
                    }
                    label={{
                      value: "ROAS",
                      angle: 90,
                      position: "insideRight",
                      fill: "#3b82f6",
                      fontSize: 10,
                      offset: -5,
                    }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return (
                        <div className="bg-background border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                          <p className="text-muted-foreground text-xs mb-1">
                            {label}
                          </p>
                          <table>
                            <tbody>
                              {payload.map((e: ChartTooltipEntry) => (
                                <tr key={getTooltipKey(e)}>
                                  <td className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full"
                                      style={{ backgroundColor: e.color }}
                                    />
                                    {e.dataKey === "ticketMedio"
                                      ? "Ticket Médio"
                                      : "ROAS"}
                                  </td>
                                  <td className="text-right font-medium text-foreground pl-4">
                                    {e.dataKey === "roas"
                                      ? Number(e.value).toLocaleString(
                                          "pt-BR",
                                          {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          },
                                        )
                                      : new Intl.NumberFormat("pt-BR", {
                                          style: "currency",
                                          currency: "BRL",
                                        }).format(getTooltipNumber(e))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(v: string) =>
                      v === "ticketMedio" ? "Ticket Médio" : "ROAS"
                    }
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="ticketMedio"
                    stroke="#6b7280"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="roas"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabela de Campanhas */}
      <StandardizedTable
        title="CAMPANHA"
        columns={
          selectedPlatform === "google"
            ? colunasGoogleCampanhas
            : colunasMetaCampanhas
        }
        data={getTableItems("campaigns")}
        total={getTableTotal("campaigns")}
        pagination={{
          ...getTablePagination("campaigns"),
          onPageChange: (p: number) => {
            setCampaignsPage(p);
            fetchCampaignsPage(p);
          },
        }}
        loading={tablesLoading}
        className="mt-8"
      />

      {/* Tabelas Meta: Conjunto de Anúncios + Nome do Anúncio */}
      {selectedPlatform === "meta" && (
        <>
          <StandardizedTable
            title="CONJUNTO DE ANÚNCIOS"
            columns={buildColunasAdsets(adsetsData)}
            data={adsetsData}
            total={adsetsTotal}
            pagination={{
              current: adsetsPag.current_page,
              total: adsetsPag.total_results,
              pageSize: 20,
              onPageChange: (p: number) => {
                setAdsetsPage(p);
                fetchAdsets(p);
              },
            }}
            loading={adsetsLoading}
            className="mt-8"
          />

          <div className="mt-8">
            <StandardizedTable
              title="NOME DO ANÚNCIO"
              columns={buildColunasAds(adNamesData)}
              data={adNamesData}
              total={adNamesTotal}
              pagination={{
                current: adNamesPag.current_page,
                total: adNamesPag.total_results,
                pageSize: 20,
                onPageChange: (p: number) => {
                  setAdNamesPage(p);
                  fetchAdNames(p);
                },
              }}
              loading={adNamesLoading}
            />
          </div>
        </>
      )}

      {/* Tabelas Google: Produtos + Termos de Pesquisa (mantidas) */}
      {selectedPlatform === "google" && (
        <>
          <StandardizedTable
            title="Produtos (Shopping)"
            columns={colunasGoogle.products}
            data={getTableItems("products")}
            total={getTableTotal("products")}
            pagination={{
              ...getTablePagination("products"),
              onPageChange: (p: number) => {
                setProductsPage(p);
                fetchProductsPage(p);
              },
            }}
            loading={tablesLoading}
            className="mt-8"
          />
          <StandardizedTable
            title="Termos de Pesquisa"
            columns={colunasGoogle.searchTerms}
            data={getTableItems("search-terms")}
            total={getTableTotal("search-terms")}
            pagination={{
              ...getTablePagination("search-terms"),
              onPageChange: (p: number) => {
                setSearchTermsPage(p);
                fetchSearchTermsPage(p);
              },
            }}
            loading={tablesLoading}
            className="mt-8"
          />
        </>
      )}
    </div>
  );
};

export default ADSPage;
